import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Learning WebRTC peer-to-peer communication, part 1",
  "description": "",
  "published": "2018-05-07T00:00:00.000Z",
  "redirect_from": ["/blog/learning-webrtc-peer-peer-communication-part-1/swizec/8359"],
  "categories": "blockchain, Front End, Livecoding, Technical, WebRTC",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <lite-youtube {...{
      "videoid": "pjoem5laCYQ",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`Remember `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/blockchain-redux"
      }}>{`blockchain-redux`}</a>{`? Yeah, I'm still working on it. Last time, we got it to `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/blockchain-redux-shares-blocks-between-clients-real-time/swizec/8320"
      }}>{`share blocks between clients in real-time`}</a>{`.`}</p>
    <p>{`But that still used Firebase as the communication channel. Our blockchain isn't really distributed. ☹️`}</p>
    <p>{`I have to fix that before my talk at `}<a parentName="p" {...{
        "href": "https://www.wearedevelopers.com/congress/"
      }}>{`WeAreDevelopers Congress`}</a>{` in Vienna. Otherwise, what's the point?`}</p>
    <p>{`WebRTC can do that for us. Get browsers to talk to each other without any servers at all.`}</p>
    <p>{`I built `}<a parentName="p" {...{
        "href": "https://swizec.github.io/webrtc-sample/"
      }}>{`a demo app`}</a>{` that uses `}<inlineCode parentName="p">{`RTCPeerConnection`}</inlineCode>{` to build a peer-to-peer connection with itself. You can see the `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/webrtc-sample/blob/master/src/WebRTCPeerConnection.js"
      }}>{`code on GitHub`}</a>{` or keep reading.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Got same page WebRTC working \\o/`}<br parentName="p"></br><br parentName="p"></br>{`Now how do I peer-to-peer 🤔 `}<a parentName="p" {...{
          "href": "https://t.co/kHTPrmpJp5"
        }}>{`pic.twitter.com/kHTPrmpJp5`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/993269639235842051"
      }}>{`May 6, 2018`}</a></blockquote>
    <p>{`Since iOS 11, WebRTC now works in all browsers people use. You can use it for real! 👍🏼`}</p>
    <p><a parentName="p" {...{
        "href": "https://swizec.github.io/webrtc-sample/"
      }}>{`Try my demo here`}</a>{`. I'd embed an iframe, but browser security rules say no.`}</p>
    <p>{`WebRTC offers 3 APIs:`}</p>
    <ul>
      <li parentName="ul">{`get video and audio from devices `}<a parentName="li" {...{
          "href": "https://www.html5rocks.com/en/tutorials/webrtc/basics/#toc-mediastream"
        }}>{`(`}<inlineCode parentName="a">{`MediaStream`}</inlineCode>{`)`}</a></li>
      <li parentName="ul">{`establish peer-to-peer connections `}<a parentName="li" {...{
          "href": "https://www.html5rocks.com/en/tutorials/webrtc/basics/#toc-rtcpeerconnection"
        }}>{`(`}<inlineCode parentName="a">{`RTCPeerConnection`}</inlineCode>{`)`}</a></li>
      <li parentName="ul">{`send arbitrary data `}<a parentName="li" {...{
          "href": "https://www.html5rocks.com/en/tutorials/webrtc/basics/#toc-rtcdatachannel"
        }}>{`(`}<inlineCode parentName="a">{`RTCDataChannel`}</inlineCode>{`)`}</a></li>
    </ul>
    <p>{`In this article, we'll use 2 of them. Media and peer connection.`}</p>
    <h2 {...{
      "id": "rtcpeerconnection-connects-clients-without-servers"
    }}>{`RTCPeerConnection connects clients without servers`}</h2>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/05/RTCPeerConnection-process-sketch.png",
        "alt": "RTCPeerConnection process sketch"
      }}></img></p>
    <p>{`You establish a connection between 2 clients on the same page like this 👇`}</p>
    <ol>
      <li parentName="ol">{`Instantiate two `}<inlineCode parentName="li">{`RTCPeerConnection`}</inlineCode>{` objects`}</li>
      <li parentName="ol">{`Add each other as ICE candidates`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`createOffer`}</inlineCode>{` on the 1st object`}</li>
      <li parentName="ol">{`set local/remote "description" on both`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`createAnswer`}</inlineCode>{` on the 2nd object`}</li>
      <li parentName="ol">{`set remote/local "description" on both`}</li>
      <li parentName="ol">{`Enjoy your direct communication`}</li>
    </ol>
    <p>{`Here's how that goes `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/webrtc-sample/blob/master/src/WebRTCPeerConnection.js"
      }}>{`in code`}</a>{`.`}</p>
    <p>{`We start with a React component that renders 2 videos and 3 buttons. It has a bunch of default state that we'll use to drive everything.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class WebRTCPeerConnection extends React.Component {
  state = {
    startDisabled: false,
    callDisabled: true,
    hangUpDisabled: true,
    servers: null,
    pc1: null,
    pc2: null,
    localStream: null,
  };

  localVideoRef = React.createRef();
  remoteVideoRef = React.createRef();

  start = () => {
    // start media devices
  };

  call = () => {
    // initiate a call
  };

  hangUp = () => {
    // hang up connection
  };

  render() {
    const { startDisabled, callDisabled, hangUpDisabled } = this.state;

    return (
      <div>
        <video
          ref={this.localVideoRef}
          autoplay
          muted
          style={{ width: "240px", height: "180px" }}
        >
          <video
            ref={this.remoteVideoRef}
            autoplay
            style={{ width: "240px", height: "180px" }}
          >
            <div>
              <button onclick={this.start} disabled>
                Start
              </button>
              <button onclick={this.call} disabled>
                Call
              </button>
              <button onclick={this.hangUp} disabled>
                Hang Up
              </button>
            </div>
          </video>
        </video>
      </div>
    );
  }
}
`}</code></pre>
    <p>{`We have 3 booleans that enable/disable our buttons, `}<inlineCode parentName="p">{`null`}</inlineCode>{` peer connections `}<inlineCode parentName="p">{`pc1`}</inlineCode>{` and `}<inlineCode parentName="p">{`pc2`}</inlineCode>{`, I don't know what `}<inlineCode parentName="p">{`servers`}</inlineCode>{` are for, and we render videos and buttons.`}</p>
    <h2 {...{
      "id": "step-1-→-start"
    }}>{`Step 1 → start`}</h2>
    <p>{`When you click the `}<inlineCode parentName="p">{`Start`}</inlineCode>{` button, we ask for audio/video permissions and start a `}<inlineCode parentName="p">{`localStream`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`start = () => {
  this.setState({
    startDisabled: true,
  });
  navigator.mediaDevices
    .getUserMedia({
      audio: true,
      video: true,
    })
    .then(this.gotStream)
    .catch((e) => alert("getUserMedia() error:" + e.name));
};

gotStream = (stream) => {
  this.localVideoRef.current.srcObject = stream;
  this.setState({
    callDisabled: false,
    localStream: stream,
  });
};
`}</code></pre>
    <p>{`Disable the start button with `}<inlineCode parentName="p">{`this.setState`}</inlineCode>{`, get your media with `}<inlineCode parentName="p">{`navigator.getUserMedia`}</inlineCode>{`. If you give permission, we start streaming in the `}<inlineCode parentName="p">{`localVideo`}</inlineCode>{` element and add it to state.`}</p>
    <h2 {...{
      "id": "step-2-→-call"
    }}>{`Step 2 → Call`}</h2>
    <p>{`You can now press the `}<inlineCode parentName="p">{`Call`}</inlineCode>{` button. That starts two peer connections, `}<inlineCode parentName="p">{`pc1`}</inlineCode>{` and `}<inlineCode parentName="p">{`pc2`}</inlineCode>{`, and goes through the dance to get them talking to each other.`}</p>
    <ol>
      <li parentName="ol"><inlineCode parentName="li">{`call`}</inlineCode>{` initiates the offer`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`onCreateOfferSuccess`}</inlineCode>{` updates both pcs and initiates the answer`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`onCreateAnswerSuccess`}</inlineCode>{` finishes the handshake`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`gotRemoteStream`}</inlineCode>{` wakes up and sets the second video`}</li>
    </ol>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`call = () => {
  this.setState({
    callDisabled: true,
    hangUpDisabled: false,
  });
  let { localStream } = this.state;

  let servers = null,
    pc1 = new RTCPeerConnection(servers),
    pc2 = new RTCPeerConnection(servers);

  pc1.onicecandidate = (e) => this.onIceCandidate(pc1, e);
  pc1.oniceconnectionstatechange = (e) => this.onIceStateChange(pc1, e);

  pc2.onicecandidate = (e) => this.onIceCandidate(pc2, e);
  pc2.oniceconnectionstatechange = (e) => this.onIceStateChange(pc2, e);
  pc2.ontrack = this.gotRemoteStream;

  localStream.getTracks().forEach((track) => pc1.addTrack(track, localStream));

  pc1
    .createOffer({
      offerToReceiveAudio: 1,
      offerToReceiveVideo: 1,
    })
    .then(this.onCreateOfferSuccess, (error) =>
      console.error("Failed to create session description", error.toString())
    );

  this.setState({
    servers,
    pc1,
    pc2,
    localStream,
  });
};
`}</code></pre>
    <p>{`This code is mostly boilerplate.`}</p>
    <p>{`We enable and disable the appropriate buttons, get `}<inlineCode parentName="p">{`localStream`}</inlineCode>{` from state, and instantiate `}<inlineCode parentName="p">{`servers`}</inlineCode>{`, `}<inlineCode parentName="p">{`pc1`}</inlineCode>{`, and `}<inlineCode parentName="p">{`pc2`}</inlineCode>{`.`}</p>
    <p>{`Both `}<inlineCode parentName="p">{`pc*`}</inlineCode>{` objects get a bunch of event listeners. `}<inlineCode parentName="p">{`onIceCandidate`}</inlineCode>{` will connect them to each other, `}<inlineCode parentName="p">{`onIceStateChange`}</inlineCode>{` just prints debugging info, and `}<inlineCode parentName="p">{`gotRemoteStream`}</inlineCode>{` will add it to the right `}<inlineCode parentName="p">{`<video>`}</inlineCode>{` element.`}</p>
    <p>{`Then we take all tracks from `}<inlineCode parentName="p">{`localStream`}</inlineCode>{` (audio and video) and add them to the first client. After that `}<inlineCode parentName="p">{`pc1`}</inlineCode>{` creates an offer to receive its video and audio.`}</p>
    <p>{`When all that's done, we update component state.`}</p>
    <h3 {...{
      "id": "oncreateoffersuccess"
    }}>{`onCreateOfferSuccess`}</h3>
    <p>{`After `}<inlineCode parentName="p">{`pc1`}</inlineCode>{` successfully creates an offer to be received, we update local and remote descriptions in our clients. I'm not sure what these "descriptions" are, but it's where the important stuff happens.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`onCreateOfferSuccess = (desc) => {
  let { pc1, pc2 } = this.state;

  pc1.setLocalDescription(desc).then(
    () => console.log("pc1 setLocalDescription complete createOffer"),
    (error) =>
      console.error(
        "pc1 Failed to set session description in createOffer",
        error.toString()
      )
  );

  pc2.setRemoteDescription(desc).then(
    () => {
      console.log("pc2 setRemoteDescription complete createOffer");
      pc2
        .createAnswer()
        .then(this.onCreateAnswerSuccess, (error) =>
          console.error(
            "pc2 Failed to set session description in createAnswer",
            error.toString()
          )
        );
    },
    (error) =>
      console.error(
        "pc2 Failed to set session description in createOffer",
        error.toString()
      )
  );
};
`}</code></pre>
    <p><inlineCode parentName="p">{`pc1`}</inlineCode>{` updates its local description, and `}<inlineCode parentName="p">{`pc2`}</inlineCode>{` updates its remote description. `}<inlineCode parentName="p">{`pc2`}</inlineCode>{` also creates an answer, which I think is akin to saying `}<em parentName="p">{`"Okay, I accepted your offer, let's do this"`}</em>{`.`}</p>
    <h3 {...{
      "id": "oncreateanswersuccess"
    }}>{`onCreateAnswerSuccess`}</h3>
    <p>{`When `}<inlineCode parentName="p">{`pc2`}</inlineCode>{` successfully creates an answer, we do another round of description setting. This time in reverse order.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`onCreateAnswerSuccess = (desc) => {
  let { pc1, pc2 } = this.state;

  pc1.setRemoteDescription(desc).then(
    () => console.log("pc1 setRemoteDescription complete createAnswer"),
    (error) =>
      console.error(
        "pc1 Failed to set session description in onCreateAnswer",
        error.toString()
      )
  );

  pc2.setLocalDescription(desc).then(
    () => console.log("pc2 setLocalDescription complete createAnswer"),
    (error) =>
      console.error(
        "pc2 Failed to set session description in onCreateAnswer",
        error.toString()
      )
  );
};
`}</code></pre>
    <p><inlineCode parentName="p">{`pc1`}</inlineCode>{` sets its remote description and `}<inlineCode parentName="p">{`pc2`}</inlineCode>{` sets its local description. I think this acknowledges that, from `}<inlineCode parentName="p">{`pc1`}</inlineCode>{`'s perspective, it is local to itself and `}<inlineCode parentName="p">{`pc2`}</inlineCode>{` is remote, and vice-versa for `}<inlineCode parentName="p">{`pc2`}</inlineCode>{`.`}</p>
    <p>{`¯`}{`\\`}{`_`}{`(ツ)`}{`_`}{`/¯`}</p>
    <p>{`At this point, we have two video streams talking to each other on the same page.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Got same page WebRTC working \\o/`}<br parentName="p"></br><br parentName="p"></br>{`Now how do I peer-to-peer 🤔 `}<a parentName="p" {...{
          "href": "https://t.co/kHTPrmpJp5"
        }}>{`pic.twitter.com/kHTPrmpJp5`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/993269639235842051"
      }}>{`May 6, 2018`}</a></blockquote>
    <h3 {...{
      "id": "onicecandidate"
    }}>{`onIceCandidate`}</h3>
    <p>{`At some point during all this, both `}<inlineCode parentName="p">{`pc*`}</inlineCode>{`s say that they've got an ICE candidate. Don't know when exactly that happens, but it gives us a chance to tell each client who they're talking to.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`onIceCandidate = (pc, event) => {
  let { pc1, pc2 } = this.state;

  let otherPc = pc === pc1 ? pc2 : pc1;

  otherPc.addIceCandidate(event.candidate).then(
    () => console.log("addIceCandidate success"),
    (error) => console.error("failed to add ICE Candidate", error.toString())
  );
};
`}</code></pre>
    <p>{`We guess the other client and add it as a candidate. If we had more than 2, this could get tricky.`}</p>
    <h2 {...{
      "id": "step-3-hangup"
    }}>{`Step 3: HangUp`}</h2>
    <p>{`Hanging up is easy. You close both clients.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`hangUp = () => {
  let { pc1, pc2 } = this.state;

  pc1.close();
  pc2.close();

  this.setState({
    pc1: null,
    pc2: null,
    hangUpDisabled: true,
    callDisabled: false,
  });
};
`}</code></pre>
    <h2 {...{
      "id": "the-tricky-part"
    }}>{`The tricky part`}</h2>
    <p>{`This works great as a tech demo. And that's when your dreams are shattered.`}</p>
    <p>{`The first part of your connection, where two clients find each other, is called signaling. The WebRTC spec doesn't say anything about signaling.`}</p>
    <p>{`It's easy in a single page, two client demo. Both clients are right there, in memory. And there's only two clients to boot.`}</p>
    <p>{`But in the real world, you want those clients to run in different browsers, on different machines, far apart in the world. How do you make them find each other? What if there's thousands?`}</p>
    <p>{`Well, you need some sort of communication channel that knows where all the clients are and can say `}<em parentName="p">{`"Yo, connect here. You, over there!"`}</em>{`. A central server of some sort…`}</p>
    <p>{`That won't do for a distributed decentralized blockchain ☹️`}</p>
    <p>{`Next step: Serverless WebRTC signaling. Stay tuned.`}</p>
    <p>{`<`}{`strong`}<em parentName="p">{`This is a Livecoding Recap – an almost-weekly post about interesting things discovered while livecoding. Usually shorter than 500 words. Often with pictures. Livecoding happens almost `}<strong parentName="em">{`every Sunday at 2pm PDT`}</strong>{` on multiple channels. You should subscribe to `}<a parentName="em" {...{
          "href": "https://www.youtube.com/TheSwizec"
        }}>{`My Youtube`}</a>{` channel to catch me live.`}</em></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      